import { Suspense } from "react"
import { ArrowLeft } from "react-feather"
import { useNavigate, useParams } from "react-router-dom"
import { toast } from "sonner"
import { roomApi } from "@/api/client/room"
import { tenantApi } from "@/api/client/tenant"
import type { TenantCreateBodyPurpose } from "@/api/generated/models/tenantCreateBodyPurpose"
import { getErrorMessage } from "@/api/instance"
import { TenantForm, type TenantFormValues } from "@/components/room"
import { toOccupantsPayload } from "@/components/room/tenant-form/TenantForm"
import { ErrorPage } from "@/components/ui/error"
import { ReactErrorBoundary } from "@/components/ui/error-boundary"
import { Spinner } from "@/components/ui/spinner/Spinner"

function RoomTenantAddPageContent({ propertyId, roomId }: { propertyId: string; roomId: string }) {
  const navigate = useNavigate()

  // tenantCreateのmutationを取得
  const { mutateAsync: createTenant, isPending } = tenantApi.useTenantCreate({
    mutation: {
      onError: (error) =>
        toast.error("賃借人の追加に失敗しました", { description: getErrorMessage(error) }),
    },
  })

  const { data: roomData } = roomApi.useRoomGetSuspense(roomId, {
    withProperty: "true",
    withPropertyOwner: "true",
  })
  if (roomData.room.property?.owner?.echoesOwnerId) {
    return (
      <ErrorPage
        title="エラー"
        message="Echoes連携物件には賃借人を追加できません。"
        backPath={`/properties/${propertyId}/rooms/${roomId}`}
      />
    )
  }

  const handleSubmit = async (values: TenantFormValues) => {
    try {
      const requestData = {
        roomId: Number(roomId),
        name: values.name,
        nameKana: values.nameKana,
        phone1: values.phone1,
        phone2: values.phone2,
        phone3: values.phone3,
        email: values.email,
        birthday: values.birthday && values.birthday.length > 0 ? values.birthday : null,
        startDate: values.startDate,
        rent: Number(values.rent || 0),
        commonAreaFee: Number(values.commonAreaFee || 0),
        isParking: values.isParking,
        parkingFee: Number(values.parkingFee || 0),
        parkingNumber: values.parkingNumber || "",
        otherFees: values.otherFees.map((fee) => ({
          label: fee.name,
          price: Number(fee.baseAmount),
          tax: Number(fee.tax),
        })),
        purpose: values.purpose as TenantCreateBodyPurpose,
        occupants: toOccupantsPayload(values.occupants),
      }

      await createTenant({ data: requestData })

      toast.success("賃借人を追加しました")
      navigate(`/properties/${propertyId}/rooms/${roomId}`, { state: { refetch: true } })
    } catch {
      // 失敗の表示は mutation の onError が担当する
    }
  }

  const handleCancel = () => {
    navigate(`/properties/${propertyId}/rooms/${roomId}`)
  }

  return (
    <ReactErrorBoundary>
      <div>
        <div className="flex items-center gap-2 p-6">
          <button
            type="button"
            onClick={() => navigate(`/properties/${propertyId}/rooms/${roomId}`)}
            className="flex items-center justify-center w-7 h-7 bg-white rounded-[5px] shadow-[0px_2px_8px_0px_rgba(19,46,90,0.05)]"
          >
            <ArrowLeft size={14} className="text-[#354267]" />
          </button>
          <span className="text-sm font-semibold text-[#3f4652]">部屋詳細に戻る</span>
        </div>

        <div className="max-w-[895px] mx-auto px-6 pb-8">
          <TenantForm
            roomId={Number(roomId)}
            defaultValues={{
              purpose: "1",
              name: "",
              nameKana: "",
              phone1: "",
              phone2: "",
              phone3: "",
              email: "",
              birthday: "",
              startDate: "",
              rent: "",
              commonAreaFee: "",
              isParking: false,
              parkingNumber: "",
              parkingFee: "",
              otherFees: [],
              occupants: [],
            }}
            onSubmit={handleSubmit}
            onCancel={handleCancel}
            isSubmitting={isPending}
            submitLabel="登録する"
          />
        </div>
      </div>
    </ReactErrorBoundary>
  )
}

// 直接 URL ロード時は ProtectedRoute の同期更新中に suspend するため、
// Suspense 境界が無いと React が throw して白画面になる(RoomEditPage と同じ構成)。
export function RoomTenantAddPage() {
  const { propertyId, roomId } = useParams<{
    propertyId: string
    roomId: string
  }>()

  if (!propertyId || Number.isNaN(Number(propertyId))) {
    return (
      <ErrorPage
        title="エラー"
        message="物件IDが指定されていません。URLを確認してください。"
        backPath="/properties"
      />
    )
  }

  if (!roomId || Number.isNaN(Number(roomId))) {
    return (
      <ErrorPage
        title="エラー"
        message="部屋IDが指定されていません。URLを確認してください。"
        backPath={`/properties/${propertyId}`}
      />
    )
  }

  return (
    <ReactErrorBoundary>
      <Suspense
        fallback={
          <div className="p-10 flex flex-col items-center justify-center">
            <Spinner className="text-slate-600 size-10" />
          </div>
        }
      >
        <RoomTenantAddPageContent propertyId={propertyId} roomId={roomId} />
      </Suspense>
    </ReactErrorBoundary>
  )
}
